import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import { firstUnseenEvent } from '../../../shared/network/visual-history.js';

/** Presented cues kept for consumers; each reads every snapshot, so this only has to outlast the gap between two. */
const KEPT = 128;
/** Snapshots come 15 times a second: a held cue goes out on the snapshot nearest its contact frame, not the first one after it. */
const RELEASE_SLACK = 1 / 30;

type Held = { due: number; events: CombatVisualEvent[] };

/**
 * Presentation only. A blow without a windup (a monster's attack, a free strike, an Opportunity Attack) reaches the browser at the moment the server
 * applies it, which is when its swing clip begins. The swing starts at once; its cue and everything that follows from the same strike (the hit, the
 * miss, the block, the interposing ally, a knock Prone) are held until the clip's contact frame, so every consumer (flinch, blood, sound, numbers,
 * voices) sees the blow land when the blade does. Attacks that already played through a windup land on their cue and pass straight through.
 *
 * Presented cues are renumbered in the order they are released, never below their server id, so consumers keep their "newer id is news"
 * bookkeeping, even one that saw the server's list before.
 */
export class StrikeTiming {
  private revision = -1;
  private seen = 0;
  private nextId = 0;
  private held: Held[] = [];
  private shown: CombatVisualEvent[] = [];
  /** Swings held by the last `present`: start their clips now (`lead` is the time from clip start to contact). */
  swings: { event: CombatVisualEvent; lead: number }[] = [];
  /** `lead` gives the seconds from a swing cue to its contact frame, or 0 when the cue lands a blow already shown through its windup. */
  constructor(private lead: (event: CombatVisualEvent, at: number) => number) {}
  present(snapshot: WorldSnapshot): WorldSnapshot {
    this.swings = [];
    if (snapshot.revision !== this.revision) {
      this.revision = snapshot.revision; this.held = []; this.shown = []; this.nextId = 0;
      // History keeps its place: consumers skip it, or replay a world's opening, just as they would the server's list.
      this.seen = firstUnseenEvent(snapshot);
      for (const event of snapshot.visualEvents) if (event.id <= this.seen) this.show(event);
    }
    for (const held of this.held) if (snapshot.gameTime >= held.due - RELEASE_SLACK) for (const event of held.events) this.show(event);
    this.held = this.held.filter(held => snapshot.gameTime < held.due - RELEASE_SLACK);
    const strikes: { swing: CombatVisualEvent; held: Held }[] = [];
    for (const event of snapshot.visualEvents) {
      if (event.id <= this.seen) continue;
      this.seen = event.id;
      let strike: (typeof strikes)[number] | undefined;
      for (let index = strikes.length - 1; index >= 0 && !strike; index--) if (sameStrike(strikes[index].swing, event)) strike = strikes[index];
      if (strike) { strike.held.events.push(event); continue; }
      const lead = event.kind === 'strike' ? this.lead(event, snapshot.gameTime) : 0;
      if (lead <= 0) { this.show(event); continue; }
      const held = { due: snapshot.gameTime + lead, events: [event] };
      this.held.push(held); strikes.push({ swing: event, held }); this.swings.push({ event, lead });
    }
    return { ...snapshot, visualEvents: this.shown.slice() };
  }
  private show(event: CombatVisualEvent) {
    this.nextId = Math.max(this.nextId + 1, event.id);
    this.shown.push({ ...event, id: this.nextId });
    if (this.shown.length > KEPT) this.shown.shift();
  }
}

/** Whether `event`, emitted in the same tick after `swing`, is part of that blow's outcome. */
function sameStrike(swing: CombatVisualEvent, event: CombatVisualEvent) {
  switch (event.kind) {
    case 'strike': case 'initiative': return false;
    case 'impact': case 'miss': case 'block': return event.actorId === swing.actorId && event.targetId === swing.targetId;
    case 'interpose': return event.targetId === swing.targetId;
    // What the blow set off: the attacker revealed, the target knocked Prone, Dazed, standing on Relentless, retaliating or arcing it on.
    default: return event.actorId === swing.actorId || event.actorId === swing.targetId;
  }
}
